import React, { useState, Fragment, useRef } from 'react';
import { useTranslate, FormDataConsumer, TextInput, SelectInput, required } from 'react-admin';
import { useForm } from 'react-final-form';

import { Drawer, EditButton, AddDeleteButton, BlockOrUnblockProfile } from '~/components';
import {
  CountryInput,
  DateInput,
  EditForm,
  BranchSelect,
  DeleteWithConfirmButton,
} from '~/components/ra';
import { UpdatePhoneNumber } from './index';
import { Individual } from '~/types';
import KycResetProgress from '~/components/KYC/KycResetProgress';
import EditActions from '~/components/EditActions';
import UpdateRiskRating from '~/components/UpdateRiskRating';
import UpdateCustomerNumber from '~/components/UpdateCustomerNumber';
import KYCLevelForceUpdate from '~/components/KYC/KYCLevelForceUpdate';
import KYCLevelCheck from '~/components/KYC/KYCLevelCheck';
import { useEditMode } from '~/hooks/useEditModeContext';

const label = (key: string): string => `resources.individuals.fields.${key}`;

type FormData = {
  formData: Individual;
  [x: string]: any;
};

interface AddNamesProps {
  addNames: string[];
  index: number;

  [x: string]: any;
}

const AddNamesField = (props: AddNamesProps) => {
  const { addNames, index, ...rest } = props;

  const translate = useTranslate();
  const translateLabel = (key: string): string => translate(label(key));

  const form = useForm<Individual>();

  return (
    <AddDeleteButton
      {...rest}
      onDeleteClick={() => {
        if (addNames) {
          form.change(
            'addNames',
            addNames.filter((item, i) => i !== index)
          );
        }
      }}
    >
      <TextInput
        source={`addNames[${index}]`}
        label={`${translateLabel('additionalName')} (${index + 1})`}
        fullWidth
      />
    </AddDeleteButton>
  );
};

interface Props {
  record: Individual;
  resource: string;
  permissions: string[];
}

const EditAccountInfo = (props: Props) => {
  const { record, resource, permissions } = props;
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const { editMode } = useEditMode();

  const translate = useTranslate();
  const translateLabel = (key: string): string => translate(label(key));

  const formRef = useRef<any>(null);

  const focusLastAddNameInput = (e: any, lastIndex: number) => {
    e.target && e.target.blur();
    setTimeout(() => {
      const input = formRef.current?.getElementsByTagName('input')[`addNames[${lastIndex}]`];
      if (input) input.focus();
    }, 0);
  };
  const menuItems = [
    permissions?.includes('individual.block') && record?.status === 'ACTIVE' && (
      <BlockOrUnblockProfile
        key='block'
        profileId={record.id}
        profileType='INDIVIDUAL'
        action='block'
      />
    ),
    permissions?.includes('individual.unblock') && record?.status === 'BLOCKED' && (
      <BlockOrUnblockProfile
        key='unblock'
        profileId={record.id}
        profileType='INDIVIDUAL'
        action='unblock'
      />
    ),
    permissions?.includes('kyc.workflow.start') && (
      <KYCLevelCheck key='kyc' record={record} resource={resource} permissions={permissions} />
    ),
    permissions?.includes('kyc.resetProgress') && (
      <KycResetProgress key='kyc-reset' record={record} resource={resource} />
    ),
    permissions?.includes('individual.forceKYCLevelUpdate') && (
      <KYCLevelForceUpdate key='kyc-force-update' record={record} resource={resource} />
    ),
    permissions?.includes('individual.updatePhoneNumber') && (
      <UpdatePhoneNumber key='update-phone' record={record} permissions={permissions} />
    ),
    permissions?.includes('individual.updateRiskRating') && (
      <UpdateRiskRating key='update-risk' profileId={record?.id} profileType='INDIVIDUAL' />
    ),
    permissions?.includes('individual.updateCustomerNumber') && (
      <UpdateCustomerNumber
        key='update-customer-number'
        profileId={record?.id!}
        profileType='INDIVIDUAL'
      />
    ),
    permissions?.includes('individual.delete') && (
      <DeleteWithConfirmButton key='delete-individual' record={record} resource={resource} />
    ),
  ].filter(Boolean);

  if (editMode === 'CHANGELOG') return null;

  if (editMode === 'DRAFT') {
    return (
      <div id='section-actions'>
        <EditButton
          label={translate('ra.action.edit')}
          onClick={handleOpen}
          disabled={
            !permissions?.includes('individual.update') &&
            !permissions?.includes('individual.create')
          }
        />
        <Drawer
          heading={translateLabel('editProfileInfo')}
          open={open}
          onClose={handleClose}
          editMode={editMode}
        >
          <div ref={formRef}>
            {record && (
              <EditForm
                resource='individuals'
                record={record}
                closeParent={handleClose}
                withSaveConfirmation={false}
              >
                <FormDataConsumer>
                  {(props: FormData) => {
                    const { formData } = props;
                    const { addNames } = formData;
                    const lastAddNameIndex = (addNames && addNames.length) || 0;
                    return (
                      <Fragment>
                        <TextInput source='firstName' validate={required()} autoFocus fullWidth />
                        <TextInput source='lastName' validate={required()} fullWidth />
                        {addNames?.map((item, index) => (
                          <AddNamesField key={index} addNames={addNames} index={index} />
                        ))}
                        <TextInput
                          source={`addNames[${addNames?.length || 0}]`}
                          label={`${translateLabel('additionalName')} (${lastAddNameIndex + 1})`}
                          fullWidth
                          onChange={(e: any) => focusLastAddNameInput(e, lastAddNameIndex)}
                        />
                        <DateInput
                          label={label('birthDate')}
                          source='birthDate'
                          options={{ fullWidth: true }}
                        />
                        <SelectInput
                          source='gender'
                          allowEmpty
                          label={label('gender._')}
                          emptyText='-'
                          choices={[
                            { id: 'MALE', name: label('gender.MALE') },
                            { id: 'FEMALE', name: label('gender.FEMALE') },
                          ]}
                          fullWidth
                        />
                        <CountryInput source='nationality' label={label('nationality')} fullWidth />
                        <TextInput source='placeOfBirth' fullWidth />
                        <BranchSelect record={record} source='branch' fullWidth />
                      </Fragment>
                    );
                  }}
                </FormDataConsumer>
              </EditForm>
            )}
          </div>
        </Drawer>
      </div>
    );
  }

  return (
    <Fragment>
      <div>
        <EditActions menuItems={menuItems} />
      </div>
    </Fragment>
  );
};

export default EditAccountInfo;
